import type { FunctionComponent, MouseEvent, MouseEventHandler } from 'react';
import { useCallback, useState } from 'react';

import { ComponentWrapper } from '@trello/component-wrapper';
import type {
  ContentType,
  LinkEventClickHandler,
  MentionEventHandler,
  SmartCardEventClickHandler,
} from '@trello/editor/renderer';
import { LazyTrelloRenderer } from '@trello/editor/renderer';
import { client } from '@trello/graphql';
import { idCache } from '@trello/id-cache';
import { useBoardId, useCardId, useWorkspaceId } from '@trello/id-context';
import { isSpecialMention } from '@trello/mentions';
import {
  dangerouslyConvertPrivacyString,
  type UGCString,
} from '@trello/privacy';
import { RouteId, useRouteId } from '@trello/router';
import { navigate } from '@trello/router/navigate';
import { isTrelloAttachmentUrl } from '@trello/urls';

import { PopOver } from 'app/scripts/views/lib/PopOver';
import { LazySingleAttachmentViewer } from 'app/src/components/AttachmentViewer';
import { LazyProfileCard } from 'app/src/components/ProfileCard';
import type { ProfileCardQuery } from 'app/src/components/ProfileCard/ProfileCardQuery.generated';
import { ProfileCardDocument } from 'app/src/components/ProfileCard/ProfileCardQuery.generated';
import { userGeneratedLinkHandler } from 'app/src/userGeneratedLinkHandler';
import { useContentRendererCardFragment } from './ContentRendererCardFragment.generated';

export interface ContentRendererProps {
  contentType: ContentType;
  text: UGCString | string;
  emoji?: Record<string, string>;
  onClick?: MouseEventHandler;
  className?: string;
  contentId?: string;
}

const emptyObj = {};
export const ContentRenderer: FunctionComponent<ContentRendererProps> = ({
  contentType,
  text,
  emoji = emptyObj,
  onClick,
  className,
  contentId,
}) => {
  const workspaceId = useWorkspaceId();
  const boardId = useBoardId();
  const cardId = useCardId();

  const routeId = useRouteId();
  const isCardBack = routeId === RouteId.CARD;

  const [attachmentId, setAttachmentId] = useState<string | null>(null);

  const { data: cardData } = useContentRendererCardFragment({
    from: { id: cardId || null },
  });

  const attachments = cardData?.attachments;

  // If the click was on a link or smart card within the rendered content, we
  // want to handle the click differently from the default behavior. If the
  // link is a Trello route, we want to navigate to it using the router.
  // Otherwise, we want to open non-Trello routes in a new tab.
  // We also want to stop propagation so this click event doesn't bubble up and
  // trigger "editing" mode for the description.
  const handleLinkClick = useCallback<
    LinkEventClickHandler | SmartCardEventClickHandler
  >((event) => {
    event.stopPropagation();
    userGeneratedLinkHandler(
      event.nativeEvent as unknown as MouseEvent,
      event.currentTarget,
      undefined,
      (clickedAttachmentId: string) => {
        setAttachmentId(clickedAttachmentId);
      },
    );
  }, []);

  // If the user clicks on a mention within the rendered content, we want to
  // check if the mention is for a member of the board or workspaces and if so,
  // show the profile card for that user.
  // We also want to stop propagation so this click event doesn't bubble up and
  // trigger "editing" mode for the description.
  // NOTE: This is using the legacy popover because we don't have any obvious
  // means to pass a ref to the mention node. This could be refactored to use
  // a UserProfileProvider and custom UserProfileClient.
  const handleMentionClick = useCallback<MentionEventHandler>(
    async (mentionName, mentionText, event) => {
      event?.stopPropagation();

      if (isSpecialMention(mentionName)) return;

      let memberId = idCache.getMemberId(mentionName);

      if (!memberId) {
        const { data } = await client.query<ProfileCardQuery>({
          query: ProfileCardDocument,
          variables: { memberId: mentionName },
          context: {
            operationName: 'ProfileCardQuery',
          },
        });

        memberId = data.member?.id;
      }

      if (!memberId) return;

      PopOver.toggle({
        elem: event?.target,
        hideHeader: true,
        reactElement: (
          <ComponentWrapper key={`profile-card-${memberId}`}>
            <LazyProfileCard
              memberId={memberId}
              onClose={() => PopOver.hide()}
            />
          </ComponentWrapper>
        ),
      });
    },
    [],
  );

  // If the user clicks on an image within the rendered content, we want to
  // check if the image is an attachment of the current card and if so, open it
  // in the attachment viewer.
  // We also want to stop propagation so this click event doesn't bubble up and
  // trigger "editing" mode for the description.
  const handleImageClick = useCallback<MouseEventHandler>(
    (event) => {
      const imgElement = (event.target as Element).closest('img');
      if (imgElement) {
        event.stopPropagation();
        const src = imgElement.src;
        if (isTrelloAttachmentUrl(src)) {
          const attachment = attachments?.find(
            (attachmentItem) =>
              dangerouslyConvertPrivacyString(attachmentItem.url) === src,
          );

          if (attachment) {
            if (!isCardBack && contentType === 'comment' && contentId) {
              navigate(
                `/c/${cardId}?attachmentId=${attachment.id}#comment-${contentId}`,
                {
                  trigger: true,
                },
              );
            } else {
              setAttachmentId(attachment.id);
            }
          }
        }
      }
    },
    [isCardBack, contentType, contentId, attachments, cardId],
  );

  const onCloseAttachmentViewer = useCallback(() => {
    setAttachmentId(null);
  }, []);

  // If the user clicks anywhere else in the rendered content, we want to call
  // the onClick callback prop. This can be used to add additional handling for
  // other nodes or, as in the description, used to enable other interactions.
  const handleUnhandledClick = useCallback<MouseEventHandler>(
    (event) => {
      onClick?.(event);
    },
    [onClick],
  );

  return (
    <>
      {/*
        For some reason, clicks on images don't invoke the media or unhandled
        handlers, but they do bubble up to the parent element. So we bind this
        click handler here to account for this. Ideally, we could bind this as
        a CardEventClickHandler, but that just doesn't seem to work.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onClick={handleImageClick}>
        <LazyTrelloRenderer
          contentType={contentType}
          text={text}
          boardId={boardId}
          cardId={cardId}
          workspaceId={workspaceId}
          emoji={emoji}
          className={className}
          onClickContent={handleUnhandledClick}
          onClickLink={handleLinkClick}
          onClickMention={handleMentionClick}
          onClickSmartCard={handleLinkClick}
        />
      </div>
      {attachmentId && (
        <LazySingleAttachmentViewer
          attachmentId={attachmentId}
          cardId={cardId}
          onClose={onCloseAttachmentViewer}
        />
      )}
    </>
  );
};
